import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  VpnKey as VpnKeyIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { surface } from '../theme';
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'editor',
    displayName: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordGenerated, setIsPasswordGenerated] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Reset to first page if current page is out of bounds
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(users.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(0);
    }
  }, [users.length, rowsPerPage, page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await UserService.getUsers();
      setUsers(usersData);
      // Reset to first page after fetching
      setPage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setFormData({
      username: '',
      password: '',
      role: 'editor',
      displayName: '',
    });
    setFormErrors({});
    setShowPassword(false);
    setIsPasswordGenerated(false);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (user) => {
    setIsEditMode(true);
    setEditingUserId(user.id);
    setFormData({
      username: user.username,
      password: '', // Leave password empty for editing
      role: user.role,
      displayName: user.displayName || '',
    });
    setFormErrors({});
    setShowPassword(false);
    setIsPasswordGenerated(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEditMode(false);
    setEditingUserId(null);
    setFormData({
      username: '',
      password: '',
      role: 'editor',
      displayName: '',
    });
    setFormErrors({});
    setShowPassword(false);
    setIsPasswordGenerated(false);
  };

  const generateRandomPassword = () => {
    const length = 16;
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData((prev) => ({ ...prev, password }));
    setIsPasswordGenerated(true);
    setShowPassword(true);
    // Clear password error if any
    if (formErrors.password) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.password;
        return newErrors;
      });
    }
  };

  const copyPasswordToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setSuccess('Password copied to clipboard!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Failed to copy password to clipboard');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    // Password is required for new users, optional for editing
    if (!isEditMode && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await UserService.createUser(
        formData.username,
        formData.password,
        formData.role,
        formData.displayName || undefined
      );
      setSuccess('User created successfully');
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      const updateData = {
        username: formData.username,
        role: formData.role,
        displayName: formData.displayName || undefined,
      };
      // Only include password if it's provided
      if (formData.password) {
        updateData.password = formData.password;
      }
      await UserService.updateUser(editingUserId, updateData);
      setSuccess('User updated successfully');
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenDeleteDialog = (user) => {
    setDeleteDialog({ open: true, user });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, user: null });
  };

  const handleDeleteUser = async () => {
    try {
      setError(null);
      setSuccess(null);
      await UserService.deleteUser(deleteDialog.user.id);
      setSuccess('User deleted successfully');
      handleCloseDeleteDialog();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActivation = async (user) => {
    try {
      setError(null);
      setSuccess(null);
      const newStatus = !user.isActive;
      await UserService.toggleActivation(user.id, newStatus);
      setSuccess(
        `User ${newStatus ? 'activated' : 'deactivated'} successfully`
      );
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'error';
      case 'admin':
        return 'warning';
      case 'uploader':
        return 'info';
      case 'ugcCreator':
        return 'info';
      case 'editor':
        return 'success';
      case 'partner':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getRolePriority = (role) => {
    const priority = {
      superadmin: 1,
      admin: 2,
      editor: 3,
      uploader: 4,
      partner: 5,
    };
    return priority[role] || 99; // Unknown roles go to the end
  };

  const sortedUsers = [...users].sort((a, b) => {
    return getRolePriority(a.role) - getRolePriority(b.role);
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated users
  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const currentUser = AuthService.getUser();
  const isCurrentUser = (userId) => {
    return currentUser && currentUser.id === userId;
  };
  const isSuperadmin = currentUser?.role === 'superadmin';

  const handleLoginAsUser = async (user) => {
    try {
      setError(null);
      setSuccess(null);
      await AuthService.loginAsUser(user.id);
      setSuccess(`Successfully logged in as ${user.username}. Reloading...`);
      // Reload page to update the UI with new user context
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          background: surface.glass,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={3}
        >
          <Box display='flex' alignItems='center' gap={2}>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant='h4' component='h1' fontWeight={700}>
              User Management
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1.5,
            }}
          >
            Add User
          </Button>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity='success'
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                    <Typography color='text.secondary'>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography variant='body1' fontWeight={500}>
                          {user.username}
                        </Typography>
                        {isCurrentUser(user.id) && (
                          <Chip
                            label='You'
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' color='text.secondary'>
                        {user.displayName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={getRoleColor(user.role)}
                        size='small'
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Deactivated'}
                        color={user.isActive ? 'success' : 'error'}
                        size='small'
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align='right'>
                      <Box display='flex' gap={1} justifyContent='flex-end'>
                        <IconButton
                          color='primary'
                          onClick={() => handleOpenEditDialog(user)}
                          title='Edit user'
                        >
                          <EditIcon />
                        </IconButton>
                        {isSuperadmin && !isCurrentUser(user.id) && (
                          <Button
                            variant='outlined'
                            color='primary'
                            onClick={() => handleLoginAsUser(user)}
                            size='small'
                            startIcon={<LoginIcon />}
                          >
                            Log in as user
                          </Button>
                        )}
                        <Button
                          variant='outlined'
                          color={user.isActive ? 'error' : 'success'}
                          onClick={() => handleToggleActivation(user)}
                          disabled={isCurrentUser(user.id)}
                          size='small'
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <IconButton
                          color='error'
                          onClick={() => handleOpenDeleteDialog(user)}
                          disabled={isCurrentUser(user.id)}
                          title={
                            isCurrentUser(user.id)
                              ? 'Cannot delete your own account'
                              : 'Delete user'
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={sortedUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />
      </Paper>

      {/* Create/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{isEditMode ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label='Username'
              name='username'
              value={formData.username}
              onChange={handleInputChange}
              error={!!formErrors.username}
              helperText={formErrors.username}
              margin='normal'
              required
            />
            <TextField
              fullWidth
              label='Display Name (Optional)'
              name='displayName'
              value={formData.displayName}
              onChange={handleInputChange}
              error={!!formErrors.displayName}
              helperText={formErrors.displayName}
              margin='normal'
            />
            <TextField
              fullWidth
              label='Password'
              name='password'
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={
                formErrors.password ||
                (isEditMode
                  ? isPasswordGenerated
                    ? 'Auto-generated password. Click copy to save it.'
                    : 'Leave blank to keep current password, or generate a new one'
                  : isPasswordGenerated
                  ? 'Auto-generated password. Click copy to save it.'
                  : undefined)
              }
              margin='normal'
              required={!isEditMode}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Box display='flex' gap={0.5}>
                      <Tooltip title='Generate random password'>
                        <IconButton
                          onClick={generateRandomPassword}
                          edge='end'
                          size='small'
                        >
                          <VpnKeyIcon />
                        </IconButton>
                      </Tooltip>
                      {formData.password && (
                        <>
                          <Tooltip
                            title={
                              showPassword ? 'Hide password' : 'Show password'
                            }
                          >
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge='end'
                              size='small'
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Copy password'>
                            <IconButton
                              onClick={copyPasswordToClipboard}
                              edge='end'
                              size='small'
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              select
              label='Role'
              name='role'
              value={formData.role}
              onChange={handleInputChange}
              margin='normal'
              required
            >
              <MenuItem value='editor'>Editor</MenuItem>
              <MenuItem value='uploader'>Uploader</MenuItem>
              <MenuItem value='ugcCreator'>UGC Creator</MenuItem>
              <MenuItem value='partner'>Partner (Admin + Analytics)</MenuItem>
              <MenuItem value='admin'>Admin</MenuItem>
              <MenuItem value='superadmin'>Superadmin</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={isEditMode ? handleUpdateUser : handleCreateUser}
            variant='contained'
          >
            {isEditMode ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth='sm'
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user{' '}
            <strong>{deleteDialog.user?.username}</strong>? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
